import type {
  CreateWatermarkTemplateInput,
  WatermarkTemplate,
} from '@frameio/graphql-client/generated';
import { BLOCK_SIZES, DEFAULT_VALUES } from '../constants';

/**
 * Maps backend font size to frontend block size
 * @param fontSize Backend font size
 * @returns Corresponding frontend block size
 */
export const mapFontSizeToBlockSize = (fontSize: string): string => {
  const sizeMap: { [key: string]: string } = {
    small: BLOCK_SIZES.SMALL,
    medium: BLOCK_SIZES.MEDIUM,
    large: BLOCK_SIZES.LARGE,
    huge: BLOCK_SIZES.HUGE,
  };

  return sizeMap[fontSize.toLowerCase()] || BLOCK_SIZES.MEDIUM;
};

/**
 * Uses the same calculations that the wmid service uses on the backend to
 * get default coordinates derived from a position
 * @param position string such as "MIDDLE_CENTER", "TOP_LEFT", etc
 * @returns default coordinates calculated from position
 */
export const getWmCoordinatesFromPosition = (
  position: string
): [number, number] => {
  const pos = position.toLowerCase();
  let y = 0.5;
  if (pos.includes('top')) {
    y = 1 / 24;
  } else if (pos.includes('bottom')) {
    y = 1 - 1 / 24;
  }

  let x = 0.5;
  if (pos.includes('left')) {
    x = 1 / 25;
  } else if (pos.includes('right')) {
    x = 1 - 1 / 25;
  }

  return [x, y];
};

export const transformBackendWatermarkTemplate = (
  template: WatermarkTemplate
) => {
  return {
    templateName: template?.name,
    blocks: template?.blocks?.map((block, index) => {
      const displayName = block?.dataPoints?.some(
        (dp) => dp.__typename === 'WatermarkDataPointUserName'
      );
      const displayEmail = block?.dataPoints?.some(
        (dp) => dp.__typename === 'WatermarkDataPointEmail'
      );
      const displayIP = block?.dataPoints?.some(
        (dp) => dp.__typename === 'WatermarkDataPointIpAddress'
      );
      const displayDateTime = block?.dataPoints?.some(
        (dp) => dp.__typename === 'WatermarkDataPointTimestamp'
      );
      const displayShareCreator = block?.dataPoints?.some(
        (dp) => dp.__typename === 'WatermarkDataPointUserInput'
      );
      const customText = block?.dataPoints?.find(
        (dp) => dp.__typename === 'WatermarkDataPointCustomText'
      )?.value;

      let { positionX: x, positionY: y } = block;

      // in the case of the default templates, position but not positionReferencePoint is set,
      // match the x and y coordinates with how they are calculated on the backend in wmid service
      if (!block.positionReferencePoint && block.position) {
        const [defaultX, defaultY] = getWmCoordinatesFromPosition(
          block.position
        );
        x = defaultX;
        y = defaultY;
      }

      return {
        id: `block-${Date.now() + index}`,
        position: {
          x: x || 0,
          y: y || 0,
        },
        width: DEFAULT_VALUES.WIDTH,
        displayName,
        displayEmail,
        displayIP,
        displayDateTime,
        displayShareCreator,
        customText,
        opacity: block.alpha * 100,
        rotation: block.rotation || DEFAULT_VALUES.ROTATION,
        size: mapFontSizeToBlockSize(block.fontSize),
        color: block.textColor,
        motion: block.scrollText?.toLowerCase(),
        section:
          block.positionReferencePoint?.toUpperCase() ||
          block.position?.toUpperCase(),
        textShadow: block.textShadow,
        textAlignment: block.textAlignment ?? DEFAULT_VALUES.TEXT_ALIGNMENT,
      };
    }),
  };
};

/**
 * Transforms frontend watermark data to backend format
 * @param templateData Frontend watermark template data
 * @returns Backend watermark template data
 */
export const transformFrontendDataForMutation = (
  templateData
): CreateWatermarkTemplateInput['watermarkTemplate'] => {
  return {
    name: templateData?.templateName,
    blocks: templateData?.normalizedBlocks.map((block) => ({
      name: block?.id,
      dataPoints: [
        block?.displayName && {
          type: 'USER_NAME',
        },
        block?.displayEmail && {
          type: 'EMAIL',
        },
        block?.displayIP && {
          type: 'IP_ADDRESS',
        },
        block?.displayDateTime && {
          type: 'TIMESTAMP',
        },
        block?.displayShareCreator && {
          type: 'USER_INPUT',
        },
        block?.customText && {
          customText: block.customText,
        },
      ].filter(Boolean),
      positionReferencePoint: block?.section,
      position: block?.section,
      positionX: block?.position?.x,
      positionY: block?.position?.y,
      fontSize: block?.size,
      textColor: block?.color,
      alpha: block?.opacity / 100,
      textShadow: block?.textShadow,
      scrollText: block?.motion?.toUpperCase(),
      textAlignment: block?.textAlignment || 'CENTER',
      rotation: block?.motion !== 'none' ? 0 : block?.rotation,
    })),
  };
};
